import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shareToWhatsApp } from "@/lib/share";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MessageCircle, Mail, Loader2, BellRing } from "lucide-react";

const STATE_LABELS = { missing: "Eksik", wrong: "Yanlış", damaged: "Hasarlı", returned: "İade" };

const num = (v) => Number(v) || 0;
const remainingOf = (it) => Math.max(num(it.ordered_qty) - num(it.received_qty), 0);

function pendingDetail(it) {
  const rem = remainingOf(it);
  const unit = it.unit || "";
  if (it.state === "wrong") return `Yanlış ürün — doğrusu bekleniyor (${(rem > 0 ? rem : num(it.ordered_qty))} ${unit})`;
  if (it.state === "damaged") return `Hasarlı — değişim bekleniyor (${(rem > 0 ? rem : num(it.ordered_qty))} ${unit})`;
  return `Bekleyen: ${rem} ${unit} (Sipariş ${num(it.ordered_qty)}, Gelen ${num(it.received_qty)})`;
}

/**
 * One-tap reminder to the supplier for the still-pending items of a partially
 * received order. Opens WhatsApp (wa.me) or Mail (mailto) prefilled, and records
 * the reminder server-side.
 * Props: open, onOpenChange, request, onDone?
 */
export default function ReminderDialog({ open, onOpenChange, request, onDone }) {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");

  const pending = useMemo(
    () => ((request?.receipt?.items) || []).filter((i) => !(i.done || i.resolved)),
    [request]
  );

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/suppliers");
        setSuppliers(data);
      } catch (_) {}
    })();
    setSupplierId(request?.supplier_id || "");
    setNote("");
  }, [open, request]);

  const supplier = suppliers.find((s) => s.id === supplierId);
  const supName = supplier?.name || request?.supplier_name || "";

  const buildList = () => pending.map((it, i) => `${i + 1}. ${it.product_code} — ${it.product_name} • ${pendingDetail(it)}`).join("\n");

  const baseMessage = () =>
    `Merhaba ${supName},\n\n*${request.request_no}* siparişimizde aşağıdaki kalemler henüz tam teslim edilmedi. Teslimat durumunu paylaşmanızı rica ederiz:\n${buildList()}` +
    (note ? `\n\nNot: ${note}` : "") +
    `\n\nTeşekkürler.`;

  const buildWaUrl = () => {
    const waNumber = (supplier?.phone || "").replace(/[^\d]/g, "");
    const msg = baseMessage();
    return waNumber
      ? `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
  };

  const openWhatsApp = () => {
    shareToWhatsApp({ text: baseMessage(), title: `Hatırlatma ${request.request_no}`, waUrl: buildWaUrl() });
  };

  const openMail = () => {
    const subject = `Sipariş Hatırlatma — Bekleyen Kalemler: ${request.request_no}`;
    const body = baseMessage() + (user?.name ? `\n\nBildiren: ${user.name}` : "");
    const params = new URLSearchParams();
    params.set("subject", subject);
    params.set("body", body);
    if (user?.email) params.set("cc", user.email);
    const link = document.createElement("a");
    link.href = `mailto:${encodeURIComponent(supplier?.email || "")}?${params.toString().replace(/\+/g, "%20")}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const send = async (channel) => {
    if (!supplierId) { toast.error("Lütfen tedarikçi seçin"); return; }
    // Open the native share sheet / mail client SYNCHRONOUSLY (within the click
    // gesture) before the await — otherwise navigator.share loses activation and
    // would fall back to opening the api.whatsapp.com browser tab.
    if (channel === "whatsapp") openWhatsApp();
    else openMail();
    setBusy(channel);
    try {
      await api.post(`/requests/${request.id}/remind`, {
        channel,
        supplier_id: supplierId,
        supplier_name: supName,
        note,
      });
      toast.success(`${supName} tedarikçisine hatırlatma iletildi (${channel === "whatsapp" ? "WhatsApp" : "Mail"})`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reminder-dialog" className="max-w-md p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <BellRing className="h-5 w-5 text-amber-600" /> Tedarikçiye Hatırlat
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            <span className="font-semibold text-slate-700">{pending.length}</span> bekleyen kalem için tedarikçiye WhatsApp/Mail hatırlatması gönderin.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Pending items preview */}
          <div className="border border-slate-100 rounded-md overflow-hidden max-h-44 overflow-y-auto">
            {pending.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-500 text-center">Bekleyen kalem yok.</div>
            ) : pending.map((it, idx) => (
              <div key={idx} className="px-3 py-2 border-t border-slate-100 first:border-t-0 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-900">{it.product_code}</span>
                  <span className="font-semibold text-amber-700">{STATE_LABELS[it.state] || it.state}</span>
                </div>
                <div className="text-slate-600 truncate">{it.product_name}</div>
                <div className="text-slate-500 mt-0.5">{pendingDetail(it)}</div>
              </div>
            ))}
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tedarikçi</Label>
            <Select value={supplierId} onValueChange={setSupplierId}>
              <SelectTrigger data-testid="reminder-supplier-select" className="mt-1.5 h-11">
                <SelectValue placeholder="Tedarikçi seçin" />
              </SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`reminder-supplier-${s.id}`}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Ek Not (opsiyonel)</Label>
            <Textarea
              data-testid="reminder-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="örn. Teslimat tarihini bildirir misiniz?"
              className="mt-1.5 text-sm min-h-[56px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button
              data-testid="reminder-whatsapp-btn"
              onClick={() => send("whatsapp")}
              disabled={!!busy || !supplierId || pending.length === 0}
              className="h-11 gap-1.5 bg-[#25D366] hover:bg-[#128C7E] text-white"
            >
              {busy === "whatsapp" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />} WhatsApp
            </Button>
            <Button
              data-testid="reminder-mail-btn"
              onClick={() => send("mail")}
              disabled={!!busy || !supplierId || pending.length === 0}
              variant="outline"
              className="h-11 gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              {busy === "mail" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Mail
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
